import { ROUTES, STATE_AUTH_PARAMS, TYPE_ACCOUNT } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import passKeyIcon from '@/assets/images/pngs/pass-key.png';
import LoginService from '@/services/login-service';
import bannerImg from '@/assets/images/pngs/banner-login.png';
import GoogleButton from '@/components/login-social-button/GoogleButton';
import TwitterButton from '@/components/login-social-button/TwitterButton';
import LineButton from '@/components/login-social-button/LineButton';
import { LoginRequest } from '@/types/http-payload/auth';
import { getStorage, removeStorage } from '@/utils/browsers';
import { startAuthentication } from '@simplewebauthn/browser';
import { toastify } from '@/utils/toastify';

export default observer(function LoginPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const { authStore: { login, getXInfo, generateAuthentication } } = useStore();
    const [currentCode, setCurrentCode] = useState<string | null>(null);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        if (code && code !== currentCode && state) {
            setCurrentCode(code);
            if (state) {
                setTimeout(() => {
                    handleDataReturn(state, code);
                }, 480);
            }
        }
    }, []);

    const handleCodeFromLine = async (code: string, isLogin: boolean = true) => {
        const id_token = await LoginService.exchangeCodeLine(code);
        if (!id_token) {

        } else {
            if (isLogin) {
                const body: LoginRequest = {
                    type: TYPE_ACCOUNT.LINE,
                    line_token: id_token
                };
                const res = await flowResult(login(body));
                if (res) {
                    navigate(ROUTES.home.href);
                }
            } else {
                navigate(ROUTES.finishSignUp.href, { state: { line_token: id_token, type: TYPE_ACCOUNT.LINE } });
            }
        }
    };
    const handleCodeFromGoogle = async (code: string, isLogin: boolean = true) => {
        const email = await LoginService.fetchInfoUserGoogle(code);
        if (!email) {

        } else {
            if (isLogin) {
                const body: LoginRequest = {
                    type: TYPE_ACCOUNT.GOOGLE,
                    email: email
                };
                const res = await flowResult(login(body));
                if (res) {
                    navigate(ROUTES.home.href);
                }
            } else {
                navigate(ROUTES.finishSignUp.href, { state: { email: email, type: TYPE_ACCOUNT.GOOGLE } });
            }
        }
    };

    const handleCodeFromX = async (code: string, isLogin: boolean = true) => {
        console.log(code);
        if (!code) {

        } else {
            if (isLogin) {
                const body: LoginRequest = {
                    type: TYPE_ACCOUNT.TWITTER,
                    code: code
                };
                const res = await flowResult(login(body));
                if (res) {
                    navigate(ROUTES.home.href);
                }
            } else {
                const resXInfo = await flowResult(getXInfo({ code: code }));
                navigate(ROUTES.finishSignUp.href, { state: { twitter_id: resXInfo.id, type: TYPE_ACCOUNT.TWITTER } });
            }
        }
    };

    const handleDataReturn = (state: string, code: string) => {
        switch (state) {
            case STATE_AUTH_PARAMS.googleLogin:
                handleCodeFromGoogle(code);
                return;
            case STATE_AUTH_PARAMS.googleSignUp:
                handleCodeFromGoogle(code, false);
                return;
            case STATE_AUTH_PARAMS.xLogin:
                handleCodeFromX(code);
                return;
            case STATE_AUTH_PARAMS.xSignUp:
                handleCodeFromX(code, false);
                return;
            case STATE_AUTH_PARAMS.lineLogin:
                handleCodeFromLine(code);
                return;
            case STATE_AUTH_PARAMS.lineSignUp:
                handleCodeFromLine(code, false);
                return;
        }
    };
    const handleSignUp = () => {
        navigate(ROUTES.signUp.href);
    };

    const handleLoginPassKey = async () => {
        const email = getStorage('email_passkey');

        // Kiểm tra email null/undefined hoặc sai định dạng
        const isValidEmail = (email: string | null): email is string =>
            !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (!isValidEmail(email)) {
            navigate(ROUTES.inputEmail.href, { state: { isLogin: true } });
            return;
        }

        try {
            const resAuth = await flowResult(generateAuthentication({ email }));
            const dataAuth = await startAuthentication({ optionsJSON: resAuth });

            const body: LoginRequest = {
                type: TYPE_ACCOUNT.PASSKEY,
                email,
                expectedChallenge: resAuth.challenge,
                body: dataAuth
            };

            const res = await flowResult(login(body));
            if (res) {
                navigate(ROUTES.home.href);
            }
        } catch (error) {
            console.log('error login passkey', error);
            // Optional: redirect on error
            // navigate(ROUTES.inputEmail.href, { state: { isLogin: true } });
        }
    };

    const removeAccountStored = () => {
        removeStorage(['email_passkey']);
        toastify('success', t('messages.delete_account_passkey_success'));
    };

    return (
        <div className='w-screen h-screen bg-white '>
            <div className='mx-auto flex w-full h-full flex-col justify-start items-center'>
                <img src={bannerImg} alt='bannerImg' className='w-full h-[30%]' />
                <div className='w-full px-[32px] mt-[-32px] rounded-t-[24px] bg-white pt-[32px] space-y-[24px]'>
                    <h3 className='text-center font-bold text-[24px] '>{t('words_title.sign_in')}</h3>
                    <button onClick={handleLoginPassKey} className='w-full py-[12px] font-semibold rounded-full border-borderGray border-[1px] flex justify-center items-center cursor-pointer content-center'>
                        <img src={passKeyIcon} alt='passkeyIcon' className='w-[24px] h-[24px]' />
                        <p className='ml-[10px] text-[15px] text-text '>{t('text.viaPassKey')}</p>
                    </button>
                    <GoogleButton state={STATE_AUTH_PARAMS.googleLogin} />
                    <TwitterButton state={STATE_AUTH_PARAMS.xLogin} />
                    <LineButton state={STATE_AUTH_PARAMS.lineLogin} />
                </div>
                <div className='w-full flex justify-center items-center font-semibold pt-[24px] px-[32px]'>
                    <div className='border-t-[1px] border-borderGray pt-[24px] w-full text-center'>
                        <p className='text-black'>{t('messages.dont_have_account')}</p>
                        <p onClick={handleSignUp} className='ml-[4px] text-textBlue'>{t('words_title.sign_up')}</p>
                    </div>
                </div>
                <div className='w-full flex justify-center items-center font-semibold pt-[24px] px-[32px]'>
                    <div className='border-t-[1px] border-borderGray pt-[24px] w-full text-center'>
                        <p onClick={removeAccountStored} className='ml-[4px] text-textBlue underline'>{t('words_title.remove_account_storage')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
});
